import { memo } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { DiligenceReport } from 'types';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

// Add a formatter function to format numbers with B, M, K suffixes
const formatNumberWithSuffix = (value: number): string => {
  if (value >= 1000000000) {
    return (value / 1000000000).toFixed(1) + 'B';
  } else if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  return value.toString();
};

const options = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 10,
      bottom: 10,
      left: 10,
      right: 10,
    },
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: {
        padding: 20,
        boxHeight: 12,
      },
    },
    datalabels: {
      display: true,
      anchor: 'end',
      align: 'top',
      offset: 0,
      formatter: (value: number) => formatNumberWithSuffix(value),
      font: {
        weight: 'bold',
        size: 9,
      },
      color: '#333',
    },
    tooltip: {
      callbacks: {
        label: function (context: any) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += context.parsed.y.toLocaleString();
          }
          return label;
        },
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        padding: 5,
      },
    },
    y: {
      grid: {
        borderDash: [8, 6],
        color: '#CDD5DF',
        drawBorder: false,
      },
      ticks: {
        padding: 5,
        callback: function (value: number) {
          return formatNumberWithSuffix(value);
        },
      },
    },
  },
};

interface DiligenceChartProps {
  loading: boolean;
  dataChart: DiligenceReport;
  onColumnClick?: (label: string, type: string) => void;
}

const StyledWrapperChart = styled.div`
  width: 100%;
  min-height: 400px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

function DiligenceChart({ loading, dataChart, onColumnClick }: DiligenceChartProps) {
  const data: DiligenceReport = {
    ...dataChart,
    datasets:
      dataChart &&
      dataChart.datasets &&
      dataChart.datasets.map((x) => ({
        ...x,
        barThickness: 20,
        borderRadius: 4,
        borderColor: 'transparent', // Make borders transparent
      })),
  };

  return (
    <Spin spinning={loading}>
      <StyledWrapperChart>
        <Bar
          options={options}
          data={data}
          getElementAtEvent={(elements, event) => {
            if (event.type === 'click' && elements.length) {
              const label = dataChart.labels[elements[0].index];
              const type = dataChart.datasets[elements[0].datasetIndex].label;

              onColumnClick?.(label, type);
            }
          }}
        />
      </StyledWrapperChart>
    </Spin>
  );
}

export default memo(DiligenceChart);
